import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { isSupportedSortKey } from '@src/utils/presets/sortKeys';
import { useSelector } from 'react-redux';
import { dimensionSortsAtom } from '../atoms';
import { dimensionMetricsSelector, globalOrderBySelector } from '@src/store/trends/base/selectors';
import usePersonalPresetsEnabled from '@src/hooks/usePersonalPresetsEnabled';
import { useState, useMemo, useCallback } from 'react';
import { SORT_TYPES } from '../dimension-table/utils';

export const useSort = ({
  sortByMetric,
  dimension,
  columnMetrics,
}: {
  sortByMetric: string;
  dimension: string;
  columnMetrics?: string[];
}) => {
  const enabled = usePersonalPresetsEnabled();
  const sortAtom = useMemo(
    () => selectAtom(dimensionSortsAtom, (sorts) => (enabled ? sorts[dimension] : undefined)),
    [dimension, enabled],
  );
  const savedSort = useAtomValue(sortAtom);
  const setSorts = useSetAtom(dimensionSortsAtom);
  const metrics = useSelector(dimensionMetricsSelector) as string[];
  const orderBy = useSelector(globalOrderBySelector);
  const [sortBy, setSortBy] = useState({
    key: sortByMetric as string,
    order: 'desc' as 'desc' | 'asc',
    type: SORT_TYPES.metric,
  });

  const onColumnSort = useCallback(
    ({ key, order }) => {
      const next = {
        key,
        order,
        type: key === dimension ? SORT_TYPES.dimension : SORT_TYPES.metric,
      };
      if (enabled) setSorts((current) => ({ ...current, [dimension]: next }));
      else setSortBy(next);
    },
    [dimension, enabled, setSorts],
  );

  const resolvedSort = useMemo(() => {
    if (!enabled) return sortBy;
    if (
      savedSort &&
      isSupportedSortKey(savedSort.key, dimension, columnMetrics || [...metrics, orderBy])
    ) {
      return {
        ...savedSort,
        type: savedSort.key === dimension ? SORT_TYPES.dimension : SORT_TYPES.metric,
      };
    }
    return { key: sortByMetric, order: 'desc' as const, type: SORT_TYPES.metric };
  }, [enabled, sortBy, savedSort, dimension, columnMetrics, metrics, orderBy, sortByMetric]);

  return { sortBy: resolvedSort, onColumnSort };
};

export default useSort;
